// Hideo - look of tags and formats: colour + icon (user request).
// - Icon = '' (none), one of LOOK_ICONS (FontAwesome) or a user image (PNG/JPEG/WebP data URL, shrunk to LOOK_IMG_PX px,
//   source file at most LOOK_IMG_MAX_MB MB): it lives inside the profile data, so it travels with Google Drive and with
//   the save file. Colour = '' (the app's main colour) or #rrggbb.
// - Formats: `color` and `icon` fields in data.series. Tags (per project): data.tagStyle[pid][k][lower-case name] = { color, icon }
//   (k = 'hook' | 'src' | 'saved'); renaming, merging and deleting tags move or remove them (moveTagLook, src/features/tags.ts).
// - Chosen in the icon/colour dialog (src/components/dialogs/LookDialog.vue, #lookModal), opened from Settings › Tags and
//   formats and from "Manage tags". Vue templates draw looks with LookIcon.vue / LookSwatch.vue; the HTML pieces
//   (tag labels, format marks: tagBadges, seriesMark) use lookIconHtml / lookChipStyle, ported from the legacy ui/icons.js
//   (verbatim copy in tests/fixtures/legacy/icons.js).
import type { ProfileData, TagGroup, TagLook } from '../data/types'
import { readableHex } from './theme'
import { isHex } from '../utils/color'

export const LOOK_ICONS = ['fa-star', 'fa-heart', 'fa-fire', 'fa-bolt', 'fa-lightbulb', 'fa-trophy', 'fa-crown', 'fa-rocket',
  'fa-gamepad', 'fa-futbol', 'fa-music', 'fa-film', 'fa-camera', 'fa-microphone', 'fa-tv', 'fa-book',
  'fa-newspaper', 'fa-graduation-cap', 'fa-flask', 'fa-code', 'fa-globe', 'fa-plane', 'fa-briefcase', 'fa-martini-glass',
  'fa-utensils', 'fa-dumbbell', 'fa-palette', 'fa-paw', 'fa-car', 'fa-money-bill', 'fa-face-laugh', 'fa-ghost']
/** Side of an uploaded icon, in pixels, and the largest file accepted, in MB */
export const LOOK_IMG_PX = 96
export const LOOK_IMG_MAX_MB = 2

/** A user image icon (data URL) rather than a FontAwesome name */
export const isLookImg = (v: unknown): boolean => /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(typeof v === 'string' ? v : '')
/** A #rrggbb colour, or '' (the app's main colour) */
export const lookColor = (c: unknown): string => (isHex(c) ? c : '')
/** A look with only a valid colour and a known icon or image */
export const cleanLook = (l: TagLook | null | undefined): Required<TagLook> => ({
  color: lookColor(l && l.color),
  icon: l && typeof l.icon === 'string' && (LOOK_ICONS.includes(l.icon) || isLookImg(l.icon)) ? l.icon : ''
})
/** Only the icon of a look (FontAwesome in the look's colour made readable on the current theme, or the user image);
 *  '' when there is none; HTML (same markup as LookIcon.vue) */
export function lookIconHtml(l: TagLook | null | undefined, cls = 'text-xs', img = 'w-4 h-4'): string {
  const L = cleanLook(l)
  if (!L.icon) return ''
  if (isLookImg(L.icon)) return `<img src="${L.icon}" alt="" class="${img} rounded object-cover inline-block flex-shrink-0">`
  return `<i class="fas ${L.icon} ${cls}"${L.color ? ` style="color:${readableHex(L.color)}"` : ''}></i>`
}
/** Inline style of a coloured label (text, border and light background, like the format badges); '' without a colour.
 *  The text uses the colour's readable version on the current theme: a white tag stays readable on the light theme */
export const lookChipStyle = (l: TagLook | null | undefined): string => {
  const c = lookColor(l && l.color)
  return c ? `color:${readableHex(c)};border-color:${c}55;background:${c}1a` : ''
}
/** The look has a colour or an icon */
export const hasLook = (l: TagLook | null | undefined): boolean => !!(l && (lookColor(l.color) || cleanLook(l).icon))

/** Look of a tag in a project. Without a project (bar with "All projects"): the first project in `order` (the bar's
 *  projects; by default all of them), then any other, that has a look for that tag */
export function tagLook(d: ProfileData, k: TagGroup, tag: string, pid?: string, order?: readonly string[]): Required<TagLook> {
  const S = d.tagStyle || {}, key = String(tag || '').toLowerCase()
  const from = (id: string) => S[id] && S[id][k] && S[id][k]![key]
  if (pid) return cleanLook(from(pid))
  const ids = [...(order || d.projects.map(p => p.id)), ...Object.keys(S)]
  for (const id of ids) {
    const l = from(id)
    if (hasLook(l)) return cleanLook(l)
  }
  return cleanLook(null)
}

/** Sets (or, for an empty look, removes) the look of a tag in a project */
export function setTagLook(d: ProfileData, k: TagGroup, pid: string, tag: string, look: TagLook): void {
  if (!d.tagStyle || typeof d.tagStyle !== 'object') d.tagStyle = {}
  const P = d.tagStyle[pid] || (d.tagStyle[pid] = {}), M = P[k] || (P[k] = {}), key = String(tag).toLowerCase()
  if (hasLook(look)) M[key] = cleanLook(look)
  else delete M[key]
}

/** A tag renamed or merged (to = the new name) or deleted (to = ''): its look follows it or goes. When merging into a
 *  tag that has its own look, that one stays */
export function moveTagLook(d: ProfileData, k: TagGroup, pid: string, from: string, to: string): void {
  const M = d.tagStyle && d.tagStyle[pid] && d.tagStyle[pid][k]
  if (!M) return
  const a = String(from).toLowerCase(), b = to ? String(to).toLowerCase() : ''
  if (b && M[a] && !hasLook(M[b])) M[b] = M[a]
  if (a !== b) delete M[a]
}

// ===== Library of uploaded icons (LookDialog.vue, "Your images"; user request 2/11/2026) =====
/** Most images kept in the library (the newest; each is a small LOOK_IMG_PX px picture) */
export const LOOK_LIB_MAX = 60
/** Every image icon of the profile: those used by tags, formats and own categories now, then those uploaded before
 *  (data.lookImages, newest first) even if nothing uses them any more; no doubles. used = a tag, a format or a category shows it */
export function lookLibrary(d: Pick<ProfileData, 'tagStyle' | 'series' | 'lookImages'> & Partial<Pick<ProfileData, 'categories'>>): { url: string; used: boolean }[] {
  const used = new Set<string>()
  Object.values(d.tagStyle || {}).forEach(P => Object.values(P || {}).forEach(M => Object.values(M || {}).forEach(l => { if (l && isLookImg(l.icon)) used.add(l.icon!) })))
  ;(d.series || []).forEach(s => { if (isLookImg(s.icon)) used.add(s.icon) })
  Object.values(d.categories || {}).forEach(list => (list || []).forEach(c => { if (c && isLookImg(c.icon)) used.add(c.icon) }))
  const all = [...new Set([...used, ...(d.lookImages || []).filter(isLookImg)])]
  return all.map(url => ({ url, used: used.has(url) }))
}
/** Keeps an uploaded image in the library (first place; the oldest go beyond LOOK_LIB_MAX). The caller saves */
export function addLookImage(d: ProfileData, url: string): void {
  if (!isLookImg(url)) return
  d.lookImages = [url, ...(d.lookImages || []).filter(x => x !== url)].slice(0, LOOK_LIB_MAX)
}
/** Removes an image from the library (one still used by a tag or a format stays where it is used). The caller saves */
export function removeLookImage(d: ProfileData, url: string): boolean {
  const before = (d.lookImages || []).length
  d.lookImages = (d.lookImages || []).filter(x => x !== url)
  return d.lookImages.length !== before
}

// ===== Uploaded icon =====
/** Why an uploaded file can't be an icon: 'type' (not an image), 'size' (over LOOK_IMG_MAX_MB), or '' (fine) */
export function lookFileProblem(f: { type: string; size: number }): '' | 'type' | 'size' {
  if (!/^image\//.test(f.type)) return 'type'
  if (f.size > LOOK_IMG_MAX_MB * 1024 * 1024) return 'size'
  return ''
}
/** The centred square of an image (what the icon keeps): left, top and side, in the image's pixels */
export function squareCrop(w: number, h: number): { x: number; y: number; side: number } {
  const side = Math.min(w, h)
  return { x: (w - side) / 2, y: (h - side) / 2, side }
}
/** A data URL over this length is saved as JPEG instead of PNG */
const LOOK_PNG_MAX = 40000

/** Reads an image file and gives its centre square shrunk to LOOK_IMG_PX px, as a data URL (PNG, or JPEG when the PNG
 *  is heavy). Rejects when the browser can't read the image. Check the file first with lookFileProblem. */
export function shrinkLookImage(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => {
      const img = new Image()
      img.onload = () => {
        const c = squareCrop(img.width, img.height), cv = document.createElement('canvas')
        cv.width = cv.height = LOOK_IMG_PX
        const ctx = cv.getContext('2d')
        if (!ctx) return reject(new Error('canvas'))
        ctx.drawImage(img, c.x, c.y, c.side, c.side, 0, 0, LOOK_IMG_PX, LOOK_IMG_PX)
        let url = cv.toDataURL('image/png')
        if (url.length > LOOK_PNG_MAX) url = cv.toDataURL('image/jpeg', 0.85)
        resolve(url)
      }
      img.onerror = () => reject(new Error('image'))
      img.src = String(r.result)
    }
    r.onerror = () => reject(new Error('file'))
    r.readAsDataURL(file)
  })
}
